import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download, ArrowLeft, Check, Mail, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { STEPS, TEMPLATES, EMPTY_CV, LS_KEY } from '../components/cv-builder/cv-data';
import {
  PersonalStep, ExperienceStep, EducationStep, SkillsStep,
  LanguagesStep, InterestsStep, ProjectsStep, CertificationsStep, ReferencesStep
} from '../components/cv-builder/CvStepForms';
import { TEMPLATE_COMPONENTS } from '../components/cv-builder/CvTemplates';
import { CoverLetterTab } from '../components/cv-builder/CoverLetterTab';

export default function CvBuilderPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cv');
  const [step, setStep] = useState(0);
  const [cv, setCv] = useState(() => {
    try { const saved = localStorage.getItem(LS_KEY); return saved ? JSON.parse(saved) : EMPTY_CV; }
    catch { return EMPTY_CV; }
  });
  const [template, setTemplate] = useState('classique');
  const [downloading, setDownloading] = useState(false);
  const [showRestore, setShowRestore] = useState(() => {
    try { return !!localStorage.getItem(LS_KEY); } catch { return false; }
  });
  const previewRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(cv)); } catch {}
  }, [cv]);

  const clearDraft = () => { localStorage.removeItem(LS_KEY); setCv(EMPTY_CV); setStep(0); toast.success('Brouillon supprimé'); setShowRestore(false); };
  const updateField = useCallback((key, val) => setCv(prev => ({ ...prev, [key]: val })), []);
  const progress = ((step + 1) / STEPS.length) * 100;

  const downloadPdf = async () => {
    if (!previewRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(previewRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH);
      pdf.save(`CV_${cv.personal.firstName}_${cv.personal.lastName}.pdf`);
      toast.success('CV téléchargé !');
    } catch { toast.error('Erreur lors du téléchargement'); }
    finally { setDownloading(false); }
  };

  const TemplateComp = TEMPLATE_COMPONENTS[template];

  const renderStep = () => {
    switch (STEPS[step].id) {
      case 'personal': return <PersonalStep data={cv.personal} onChange={v => updateField('personal', v)} />;
      case 'experience': return <ExperienceStep data={cv.experiences} onChange={v => updateField('experiences', v)} />;
      case 'education': return <EducationStep data={cv.education} onChange={v => updateField('education', v)} />;
      case 'skills': return <SkillsStep data={cv.skills} onChange={v => updateField('skills', v)} />;
      case 'languages': return <LanguagesStep data={cv.languages} onChange={v => updateField('languages', v)} />;
      case 'interests': return <InterestsStep data={cv.interests} onChange={v => updateField('interests', v)} />;
      case 'projects': return <ProjectsStep data={cv.projects} onChange={v => updateField('projects', v)} />;
      case 'certifications': return <CertificationsStep data={cv.certifications} onChange={v => updateField('certifications', v)} />;
      case 'références': return <ReferencesStep data={cv.références} onChange={v => updateField('références', v)} />;
      case 'preview': return (
        <div className="space-y-6" data-testid="step-preview">
          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-3">Choisir un modele</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {TEMPLATES.map(t => (
                <button key={t.id} onClick={() => setTemplate(t.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${template === t.id ? 'border-[#0D9488] bg-[#0D9488]/5 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`template-${t.id}`}>
                  <div className="text-sm font-bold text-slate-700">{t.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-700">Aperçu du CV</h3>
              <Button onClick={downloadPdf} disabled={downloading} className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="download-pdf">
                {downloading ? 'Génération...' : <><Download className="w-4 h-4 mr-2" /> Télécharger PDF</>}
              </Button>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white" style={{ maxHeight: '700px', overflowY: 'auto' }}>
              <div ref={previewRef}><TemplateComp cv={cv} /></div>
            </div>
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white" data-testid="cv-builder-page">
      <div className="bg-gradient-to-r from-[#0F766E] to-[#0D9488] py-6">
        <div className="max-w-5xl mx-auto px-4">
          <button onClick={() => navigate('/recrutement')} className="flex items-center gap-2 text-white/70 hover:text-white text-sm mb-3 transition-colors" data-testid="back-to-recruitment">
            <ArrowLeft className="w-4 h-4" /> Retour au recrutement
          </button>
          <h1 className="text-2xl font-bold text-white">{activeTab === 'cv' ? 'Constructeur de CV' : 'Lettre de motivation'}</h1>
          <p className="text-white/60 text-sm mt-1">{activeTab === 'cv' ? 'Créez votre CV professionnel en quelques minutes' : 'Générez une lettre personnalisée grâce à l\'IA'}</p>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 flex gap-0">
          <button onClick={() => setActiveTab('cv')} className={`flex items-center gap-2 px-6 py-3.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'cv' ? 'border-[#0D9488] text-[#0D9488]' : 'border-transparent text-gray-400 hover:text-gray-600'}`} data-testid="tab-cv">
            <FileText className="w-4 h-4" /> CV
          </button>
          <button onClick={() => setActiveTab('lettre')} className={`flex items-center gap-2 px-6 py-3.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'lettre' ? 'border-[#F59E0B] text-[#F59E0B]' : 'border-transparent text-gray-400 hover:text-gray-600'}`} data-testid="tab-lettre">
            <Mail className="w-4 h-4" /> Lettre de motivation
            <span className="text-[9px] bg-[#F59E0B] text-white px-1.5 py-0.5 rounded-full font-bold uppercase">IA</span>
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {activeTab === 'lettre' ? <CoverLetterTab cv={cv} /> : (
          <>
            <div className="flex justify-center gap-1 mb-3 flex-wrap" data-testid="step-nav">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const isActive = i === step;
                const isCompleted = i < step;
                return (
                  <button key={s.id} onClick={() => setStep(i)} className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg transition-all min-w-[72px] ${isActive ? 'bg-[#0D9488]/10' : ''}`} data-testid={`step-btn-${s.id}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${isActive ? 'bg-[#0D9488] text-white shadow-md shadow-[#0D9488]/30' : isCompleted ? 'bg-[#0D9488]/20 text-[#0D9488]' : 'bg-gray-100 text-gray-400'}`}>
                      {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-[10px] font-medium leading-tight text-center ${isActive ? 'text-[#0D9488]' : 'text-gray-400'}`}>{s.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="h-1.5 bg-gray-100 rounded-full mb-8 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#0F766E] via-[#0D9488] to-[#F59E0B] rounded-full transition-all duration-300" style={{ width: `${progress}%` }} data-testid="progress-bar" />
            </div>

            {showRestore && cv.personal.firstName && (
              <div className="flex items-center justify-between bg-[#0D9488]/10 border border-[#0D9488]/20 rounded-xl px-5 py-3 mb-6" data-testid="restore-banner">
                <p className="text-sm text-white/60 mt-1">Brouillon restauré automatiquement.</p>
                <button onClick={clearDraft} className="text-xs font-semibold text-red-500 hover:text-red-700 underline" data-testid="clear-draft">Recommencer à zéro</button>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#0D9488]/10 flex items-center justify-center">
                  {(() => { const Icon = STEPS[step].icon; return <Icon className="w-4 h-4 text-[#0D9488]" />; })()}
                </div>
                {STEPS[step].label}
                <span className="text-xs text-gray-400 font-normal ml-auto">Etape {step + 1}/{STEPS.length}</span>
              </h2>
              {renderStep()}
            </div>

            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="border-gray-200" data-testid="prev-step">
                <ChevronLeft className="w-4 h-4 mr-2" /> Precedent
              </Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={() => setStep(step + 1)} className="bg-[#0D9488] hover:bg-[#0F766E] text-white" data-testid="next-step">
                  Suivant <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={downloadPdf} disabled={downloading} className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="download-pdf-bottom">
                  <Download className="w-4 h-4 mr-2" /> {downloading ? 'Génération...' : 'Télécharger mon CV'}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
